import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, PencilSimple, Trash, Eye, ArrowLeft } from "@phosphor-icons/react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { toast } from "sonner";

export default function BlogAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", excerpt: "", body: "", cover_image: "", published: true });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/blog/posts", { params: { include_drafts: true } });
    setPosts(data);
  };

  useEffect(() => {
    if (user && user.role !== "admin") navigate("/blog");
    else load();
  }, [user, navigate]);

  const open = (post) => {
    if (post) {
      setEditing(post.id);
      api.get(`/blog/posts/${post.slug}`).then(({ data }) => setForm({
        title: data.title, excerpt: data.excerpt || "", body: data.body || "",
        cover_image: data.cover_image || "", published: data.published,
      }));
    } else {
      setEditing("new");
      setForm({ title: "", excerpt: "", body: "", cover_image: "", published: true });
    }
  };

  const cancel = () => { setEditing(null); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body are required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, cover_image: form.cover_image || null };
      if (editing === "new") {
        await api.post("/blog/posts", payload);
        toast.success("Post created");
      } else {
        await api.patch(`/blog/posts/${editing}`, payload);
        toast.success("Post saved");
      }
      setEditing(null);
      await load();
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!confirm("Delete this post?")) return;
    try { await api.delete(`/blog/posts/${id}`); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  if (!user) return null;

  return (
    <AppLayout>
      <div className="space-y-8 max-w-4xl" data-testid="blog-admin-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Link to="/blog" className="inline-flex items-center gap-2 text-stoneink-700 hover:text-terracotta-600 text-sm">
              <ArrowLeft size={16} /> View public blog
            </Link>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">Blog posts</h1>
            <p className="mt-2 text-stoneink-700">Create, edit, and publish. Markdown lite — use # headings and **bold**.</p>
          </div>
          {!editing && (
            <button onClick={() => open(null)} data-testid="blog-new-btn"
              className="h-11 px-5 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium flex items-center gap-2">
              <Plus weight="bold" /> New post
            </button>
          )}
        </div>

        {editing ? (
          <motion.form initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} onSubmit={save}
            className="rounded-3xl border border-sand-300 bg-white p-6 sm:p-8 shadow-ambient space-y-5"
            data-testid="blog-form">
            <Field label="Title" required>
              <input data-testid="blog-title-input" required value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full h-11 px-4 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 outline-none"
                placeholder="A short, evocative headline" />
            </Field>
            <Field label="Excerpt">
              <input data-testid="blog-excerpt-input" value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                className="w-full h-11 px-4 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 outline-none"
                placeholder="The teaser that appears on the blog list" />
            </Field>
            <Field label="Cover image URL">
              <input data-testid="blog-cover-input" value={form.cover_image}
                onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                className="w-full h-11 px-4 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 outline-none"
                placeholder="https://… (Unsplash, your CDN, etc.)" />
            </Field>
            <Field label="Body (markdown-lite + rich blocks)" required>
              <textarea data-testid="blog-body-input" required rows={18} value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                className="w-full p-4 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 outline-none font-mono text-sm leading-relaxed"
                placeholder={`## Heading\n\nA cozy intro paragraph. **Bold** matters. [Links](https://example.com) too.\n\n- A small list\n- Another item\n\n:::divider\ndivider\n:::\n\n:::callout amber\n[Optional label]\nA highlighted note in warm ochre. Use 'amber' or 'blue'.\n:::\n\n:::path\nRoom::Hall Closet\nSpot::Box 2\nItem::HDMI Cables\n:::\n\n:::features\n🎙️ | Voice input | One-sentence description.\n📦 | QR codes | Another description.\n:::\n\n:::quote\nA short, memorable line.\n— Attribution\n:::\n\n:::cta\nHeadline goes here::\nSupporting body sentence.\nprimary::Try Nestled Free::/register\nghost::See features →::/features\n:::\n\n:::tags\nHome Organization\nInventory\nQR Codes\n:::`} />
              <div className="mt-2 text-xs text-stoneink-500 leading-relaxed">
                <strong className="text-stoneink-700">Rich blocks:</strong>{" "}
                <code className="px-1.5 py-0.5 rounded bg-sand-200 text-stoneink-700">:::divider</code>{" "}
                <code className="px-1.5 py-0.5 rounded bg-sand-200 text-stoneink-700">:::callout amber|blue</code>{" "}
                <code className="px-1.5 py-0.5 rounded bg-sand-200 text-stoneink-700">:::path</code>{" "}
                <code className="px-1.5 py-0.5 rounded bg-sand-200 text-stoneink-700">:::features</code>{" "}
                <code className="px-1.5 py-0.5 rounded bg-sand-200 text-stoneink-700">:::quote</code>{" "}
                <code className="px-1.5 py-0.5 rounded bg-sand-200 text-stoneink-700">:::cta</code>{" "}
                <code className="px-1.5 py-0.5 rounded bg-sand-200 text-stoneink-700">:::tags</code>
                {" — close each with "}<code className="px-1.5 py-0.5 rounded bg-sand-200 text-stoneink-700">:::</code> on its own line.
              </div>
            </Field>
            <label className="inline-flex items-center gap-2 text-stoneink-700">
              <input data-testid="blog-published-toggle" type="checkbox" checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                className="h-5 w-5 rounded accent-terracotta-500" />
              <span className="text-sm">Published (uncheck to save as draft)</span>
            </label>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={cancel} className="h-11 px-5 rounded-xl border border-sand-300 text-stoneink-700 hover:bg-sand-200">Cancel</button>
              <button data-testid="blog-save-btn" type="submit" disabled={busy}
                className="h-11 px-6 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium disabled:opacity-60">
                {busy ? "Saving…" : editing === "new" ? "Publish" : "Save changes"}
              </button>
            </div>
          </motion.form>
        ) : posts.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-sand-300 bg-white p-10 text-center text-stoneink-700">No posts yet — write the first.</div>
        ) : (
          <div className="space-y-3">
            {posts.map((p) => (
              <div key={p.id} data-testid={`blog-admin-row-${p.id}`}
                className="rounded-2xl border border-sand-300 bg-white p-5 shadow-ambient flex flex-wrap items-center gap-4 justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="font-heading text-lg text-stoneink-900 font-medium truncate">{p.title}</div>
                  {!p.published && <span className="text-xs px-2 py-0.5 rounded-full bg-ochre-400/30 text-ochre-600 font-medium shrink-0">draft</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`/blog/${p.slug}`} className="h-9 px-3 rounded-lg hover:bg-sand-200 text-stoneink-700 inline-flex items-center gap-1.5 text-sm">
                    <Eye size={14} /> View
                  </Link>
                  <button onClick={() => open(p)} className="h-9 px-3 rounded-lg hover:bg-sand-200 text-stoneink-700 inline-flex items-center gap-1.5 text-sm">
                    <PencilSimple size={14} /> Edit
                  </button>
                  <button onClick={() => del(p.id)} className="h-9 px-3 rounded-lg hover:bg-sand-200 text-stoneink-500 hover:text-destructive inline-flex items-center gap-1.5 text-sm">
                    <Trash size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">{label}{required && " *"}</label>
    <div className="mt-2">{children}</div>
  </div>
);
